<template>
  <component
    :is="$attrs.is || 'div'"
    ref="root"
    v-bind="$attrs"
    :class="cn(
      'relative',
      `--${section.sid}-element`,
      $attrs.class
    )"
    :data-uid="section.uid"
    :data-sid="section.sid"
    v-on="$listeners"
    @click.stop="handleClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div
      class="hover-safe-zone"
      :class="{ 'is-active': hovered || selected }"
      aria-hidden="true"
    />
    <EditorToolbar
      v-model="section.name"
      :hovered="hovered"
      :selected="selected"
      :type="type"
      @delete="$emit('delete')"
      @dublicate="$emit('dublicate')"
      @favorite="$emit('favorite')"
      @move-to="to => $emit('move-to', to)"
      @add-subject="$emit('add-subject')"
    />

    <slot />
  </component>
</template>

<script>
import clsx from 'clsx'
import EditorToolbar from '@/components/builder/utils/Toolbar.vue'

export default {
  name: 'Element',
  props: {
    section: {
      type: Object,
      required: true
    },
    type: {
      type: String,
      default: 'block'
    }
  },
  data () {
    return {
      hovered: false,
      selected: false,
      onDocPointerDown: null,
      onDocKeyDown: null
    }
  },
  beforeDestroy () {
    this.unbindSelectionListeners()
  },
  components: {
    EditorToolbar
  },
  methods: {
    cn (...args) {
      return clsx(...args)
    },

    setSelected (next, event) {
      if (this.selected === next) return

      this.selected = next
      this.$emit('select', { section: this.section, selected: next, event })

      if (next) {
        this.bindSelectionListeners()
      } else {
        this.unbindSelectionListeners()
      }
    },

    bindSelectionListeners () {
      if (this.onDocPointerDown || this.onDocKeyDown) return

      // Capture phase so it still works even if inner elements call stopPropagation.
      this.onDocPointerDown = (e) => {
        // Ignore during drag flows used by the builder (old code relied on window.isDrag too).
        if (typeof window !== 'undefined' && window.isDrag) return

        const root = this.$refs.root
        if (!root) return

        // If click/tap happened outside of this element => blur.
        if (!root.contains(e.target)) {
          this.hovered = false
          this.setSelected(false, e)
        }
      }

      this.onDocKeyDown = (e) => {
        // ESC clears selection
        if (e.key === 'Escape' || e.keyCode === 27) {
          this.hovered = false
          this.setSelected(false, e)
        }
      }

      document.addEventListener('pointerdown', this.onDocPointerDown, true)
      document.addEventListener('keydown', this.onDocKeyDown, true)
    },

    unbindSelectionListeners () {
      if (this.onDocPointerDown) {
        document.removeEventListener('pointerdown', this.onDocPointerDown, true)
        this.onDocPointerDown = null
      }
      if (this.onDocKeyDown) {
        document.removeEventListener('keydown', this.onDocKeyDown, true)
        this.onDocKeyDown = null
      }
    },

    handleClick (event) {
      // Clicking focuses/activates the element.
      this.hovered = true
      this.setSelected(true, event)
      this.$store.commit('editor/CLEAR_ELEMENTS_IN_STACK')

      // Bubble selection info to the parent
      this.$emit('flow')
      this.$emit('highlight-click')
    },

    handleMouseEnter (event) {
      this.hovered = true
      this.$emit('hover', { section: this.section, hovered: true, event })
    },

    handleMouseLeave (event) {
      // If selected, keep the "active" state like the old Highliter's pressed behavior.
      if (!this.selected) {
        this.hovered = false
      }
      this.$emit('hover', { section: this.section, hovered: false, event })
    }
  }
}
</script>
<style scoped>
.hover-safe-zone {
  position: absolute;
  top: -26px;
  left: 0;
  right: 0;
  height: 26px;
  pointer-events: none;
  /* sits above the element so it can catch hover while active */
  z-index: 1000;
}

.hover-safe-zone.is-active {
  pointer-events: auto;
}
</style>